/**
 * Reports the first time an element scrolls into view, then stops watching.
 *
 * Used to defer expensive per-row work in long tables — a page of 100 payments must not fire
 * 100 file downloads for thumbnails the operator never scrolls to.
 *
 * Every caller with the same `rootMargin` shares ONE `IntersectionObserver`. One observer per
 * hook instance meant fifty of them on a page of payments, each with its own callback to run on
 * every scroll frame; the browser is built to batch many targets through one observer, and does
 * not batch across several.
 */

import { RefObject, useEffect, useState } from "react";

interface ObserverRegistry {
  observer: IntersectionObserver;
  /** What to call when a target first intersects, by target. */
  callbacks: Map<Element, () => void>;
}

/**
 * One registry per distinct `rootMargin`, for the life of the page.
 *
 * Deliberately never disconnected: it holds no reference to any element it is not currently
 * watching — a target is removed on entry and on unmount — so the only thing outliving the
 * components is the observer itself, and rebuilding one per mount is the cost this avoids.
 */
const registries = new Map<string, ObserverRegistry>();

function registryFor(rootMargin: string): ObserverRegistry {
  const existing = registries.get(rootMargin);
  if (existing) {
    return existing;
  }

  const callbacks = new Map<Element, () => void>();

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          continue;
        }

        const callback = callbacks.get(entry.target);
        if (!callback) {
          continue;
        }

        // Unregister before calling back, so a second entry for the same target in the same
        // batch cannot run it twice.
        callbacks.delete(entry.target);
        observer.unobserve(entry.target);
        callback();
      }
    },
    { rootMargin },
  );

  const registry: ObserverRegistry = { observer, callbacks };
  registries.set(rootMargin, registry);

  return registry;
}

export function useInViewOnce<T extends Element>(
  ref: RefObject<T | null>,
  rootMargin = "200px",
): boolean {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;

    const element = ref.current;
    if (!element) return;

    // No observer (jsdom, very old browsers): fall back to loading right away rather than
    // never loading at all.
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const { observer, callbacks } = registryFor(rootMargin);

    callbacks.set(element, () => setInView(true));
    observer.observe(element);

    return () => {
      callbacks.delete(element);
      observer.unobserve(element);
    };
  }, [ref, rootMargin, inView]);

  return inView;
}
